import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Home,
  Bed,
  ChefHat,
  Bath,
  Sofa,
  Briefcase,
  Baby
} from 'lucide-react';
import { Link } from 'react-router-dom';

const RoomsPage = () => {
  const rooms = [
    {
      name: 'Living Rooms',
      subtitle: 'Comfort & Style',
      description: 'Transform your living space into the perfect environment for relaxation and entertainment. Control light throughout the day while maintaining your home\'s elegant aesthetic.',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/leqxhvi8_01.%20Taupe%20Bay%20Shutters.jpg',
      benefits: [
        'Perfect climate control all year round',
        'Privacy on demand for family time',
        'Elegant style that complements any décor',
        'UV protection for furniture and artwork',
        'Energy savings through insulation',
        'Noise reduction from outside'
      ],
      recommendedStyles: ['Full Height', 'Tier on Tier'],
      recommendedMaterials: ['Premium Hardwood', 'Premium MDF'],
      recommendedLouvres: ['63mm', '76mm', '89mm'],
      specialFeatures: [
        'Mid-rail options for flexible light control',
        'Wide range of finishes to match décor',
        'Silent operation for peaceful relaxation'
      ],
      icon: Sofa,
      color: 'text-[#8CB89F]',
      bgColor: 'bg-[#8CB89F]/10'
    },
    {
      name: 'Bedrooms',
      subtitle: 'Rest & Privacy',
      description: 'Create the ideal sleep environment with complete light and privacy control. Our bedroom shutters help regulate temperature and reduce noise for perfect rest.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/587d19c8b4573aed50340d8e079badc98afc137cb37f38b7d447fc336fc69c5d.png',
      benefits: [
        'Complete blackout capability for quality sleep',
        'Superior sound insulation for peace',
        'Temperature regulation year-round',
        'Maximum privacy for personal space',
        'Easy operation even in the dark',
        'Child-safe cordless design'
      ],
      recommendedStyles: ['Full Height', 'Solid Panel'],
      recommendedMaterials: ['Premium Hardwood', 'Premium MDF'],
      recommendedLouvres: ['63mm'],
      specialFeatures: [
        'Solid panels available for complete darkness',
        'Soft-close mechanisms for quiet operation',
        'Child safety features included'
      ],
      icon: Bed,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      name: 'Kitchens',
      subtitle: 'Function & Beauty',
      description: 'Practical beauty for the heart of your home. Our kitchen shutters are designed to withstand moisture, heat, and daily use while maintaining their stunning appearance.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/4c42b25297bb9cdc957f101999155b8d77aa410c72b2933afc193f514b59c0df.png',
      benefits: [
        'Easy cleaning with simple wipe-down',
        'Humidity and heat resistant materials',
        'Practical light control while cooking',
        'Grease and stain resistant finishes',
        'Complements kitchen cabinetry perfectly',
        'Ventilation control for comfort'
      ],
      recommendedStyles: ['Café Style', 'Full Height'],
      recommendedMaterials: ['Moisture-Resistant Polymer', 'Premium Hardwood'],
      recommendedLouvres: ['63mm', '76mm'],
      specialFeatures: [
        'Easy-clean surfaces resist stains',
        'Steam and heat resistant coatings',
        'Café style perfect for privacy with light'
      ],
      icon: ChefHat,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      name: 'Bathrooms',
      subtitle: 'Moisture Resistant',
      description: 'Purpose-built for high-moisture environments. Our waterproof shutters thrive in bathrooms, providing privacy and style that won\'t warp, rot, or deteriorate.',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/oqwlncwz_bathroom.jpeg',
      benefits: [
        '100% waterproof construction',
        'Mold and mildew resistant',
        'Privacy assured at all times',
        'Easy maintenance and cleaning',
        'No warping or deterioration',
        'Elegant alternative to blinds'
      ],
      recommendedStyles: ['Full Height', 'Café Style'],
      recommendedMaterials: ['Moisture-Resistant Polymer'],
      recommendedLouvres: ['63mm', '76mm'],
      specialFeatures: [
        'Fully sealed against moisture penetration',
        'Antimicrobial coatings available',
        'Rust-free hardware and mechanisms'
      ],
      icon: Bath,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50'
    },
    {
      name: 'Home Offices',
      subtitle: 'Productivity Focus',
      description: 'Create the perfect work environment with precise light control and professional aesthetics. Reduce screen glare while maintaining a productive, comfortable workspace.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/bfa00a8509bb576802fa8f8aa2b42032b4ee8c711e9c90a9b1f89bc57712b105.png',
      benefits: [
        'Glare reduction for computer screens',
        'Professional appearance for video calls',
        'Precise light control for productivity',
        'Noise reduction for concentration',
        'Privacy for confidential work',
        'Energy efficiency for home offices'
      ],
      recommendedStyles: ['Full Height', 'Tier on Tier'],
      recommendedMaterials: ['Premium Hardwood', 'Premium MDF'],
      recommendedLouvres: ['63mm', '76mm'],
      specialFeatures: [
        'Anti-glare positioning options',
        'Silent operation for video calls',
        'Professional finishes available'
      ],
      icon: Briefcase,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50'
    },
    {
      name: 'Children\'s Rooms',
      subtitle: 'Safe & Practical',
      description: 'Child-safe shutters designed with safety as the priority. Cordless operation, easy cleaning, and durable construction perfect for active children.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/587d19c8b4573aed50340d8e079badc98afc137cb37f38b7d447fc336fc69c5d.png',
      benefits: [
        'Completely cordless for child safety',
        'Durable construction withstands play',
        'Easy cleaning for busy parents',
        'Light control for better sleep',
        'Fun colors and finishes available',
        'Grows with your child'
      ],
      recommendedStyles: ['Full Height', 'Café Style'],
      recommendedMaterials: ['Premium MDF', 'Premium Hardwood'],
      recommendedLouvres: ['63mm'],
      specialFeatures: [
        'No cords or chains for safety',
        'Impact-resistant construction',
        'Non-toxic finishes and materials'
      ],
      icon: Baby,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50'
    }
  ];

  const roomTypeGuide = {
    'High Moisture': {
      rooms: ['Bathrooms', 'Utility Rooms', 'Pool Areas'],
      material: 'Moisture-Resistant Polymer',
      color: 'text-[#8CB89F]',
      description: 'Waterproof construction essential'
    },
    'High Traffic': {
      rooms: ['Kitchens', 'Children\'s Rooms', 'Family Rooms'],
      material: 'Premium MDF or Polymer',
      color: 'text-green-600',
      description: 'Durable, easy-clean surfaces'
    },
    'Light Control': {
      rooms: ['Bedrooms', 'Media Rooms', 'Home Offices'],
      material: 'Any Material',
      color: 'text-purple-600',
      description: 'Focus on louvre size and style'
    },
    'Style Focus': {
      rooms: ['Living Rooms', 'Dining Rooms', 'Studies'],
      material: 'Premium Hardwood',
      color: 'text-orange-600',
      description: 'Natural beauty and elegance'
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Perfect Shutters for
              <span className="block text-[#8CB89F]">Every Room</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover how plantation shutters enhance different spaces in your home, 
              from bedrooms requiring complete privacy to kitchens needing practical beauty.
            </p>
          </div>
        </div>
      </section>

      {/* Room Guide Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="space-y-20">
            {rooms.map((room, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex items-start gap-12`}>
                {/* Image */}
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <div className="relative">
                    <img 
                      src={room.image} 
                      alt={room.name}
                      className="w-full h-96 object-cover rounded-2xl shadow-xl"
                    />
                    <div className={`absolute top-4 left-4 ${room.bgColor} rounded-full p-3`}>
                      <room.icon className={`h-8 w-8 ${room.color}`} />
                    </div>
                    <div className="absolute bottom-4 right-4 bg-white/90 rounded-lg px-3 py-2">
                      <Badge className={room.color}>{room.subtitle}</Badge>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2">
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">{room.name}</h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{room.description}</p>
                  
                  {/* Benefits */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Key Benefits:</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {room.benefits.map((benefit, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recommendations */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Recommended Styles:</h5>
                      {room.recommendedStyles.map((style, i) => (
                        <Badge key={i} variant="outline" className="mr-1 mb-1">{style}</Badge>
                      ))}
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Best Materials:</h5>
                      {room.recommendedMaterials.map((material, i) => (
                        <Badge key={i} variant="outline" className="mr-1 mb-1">{material}</Badge>
                      ))}
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Louvre Sizes:</h5>
                      {room.recommendedLouvres.map((louvre, i) => (
                        <Badge key={i} variant="outline" className="mr-1 mb-1">{louvre}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Special Features */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Special Features:</h4>
                    <div className="space-y-2">
                      {room.specialFeatures.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${room.color.replace('text-', 'bg-')}`}></div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Room Type Guide */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Quick Selection Guide</h2>
            <p className="text-xl text-gray-600">
              Choose the right shutter based on your room's primary requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(roomTypeGuide).map(([type, guide], index) => (
              <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <CardTitle className={`text-xl ${guide.color}`}>{type}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">{guide.description}</p>
                  <div className="mb-4">
                    <div className="font-medium text-gray-900 mb-2">Best Material:</div>
                    <Badge variant="outline" className={guide.color}>{guide.material}</Badge>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 mb-2">Room Types:</div>
                    <div className="space-y-1">
                      {guide.rooms.map((room, i) => (
                        <div key={i} className="text-sm text-gray-600">{room}</div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Rooms?</h2>
          <p className="text-xl mb-8 text-gray-300">
            Get expert advice on the perfect shutters for each room in your home with a personalized quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-[#8CB89F] hover:bg-[#7aa88d] px-8 py-4 text-lg">
                <Calculator className="mr-2 h-5 w-5" />
                Calculate Room by Room
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg"
            >
              <Home className="mr-2 h-5 w-5" />
              Book Home Survey
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RoomsPage;